import React, { FormEvent } from 'react'

import { ValidationFunction } from '@app/src/components/forms/Validations'
import { MAX_DATE, MIN_DATE } from '@app/src/constants/dates'
import styled, { elementProps } from '@app/src/constants/theme'
import { FormElementCallback } from '@app/src/hooks/useForm'

interface Props extends React.InputHTMLAttributes<HTMLInputElement> {
  clientErrorText?: string
  isRequired?: boolean
  label?: string
  name: string
  onFormElementChange?: FormElementCallback
  validations?: ValidationFunction[]
}

const DateField: React.FC<Props> = ({
  clientErrorText,
  isRequired,
  label,
  name,
  onFormElementChange,
  onInput,
  validations,
  ...props
}) => {
  const inputRef = React.useRef<HTMLInputElement | null>(null)

  React.useEffect(() => {
    if (onFormElementChange) {
      onFormElementChange(
        name,
        `${inputRef.current?.value ?? ''}`,
        isRequired ?? false,
        validations,
      )
    }
    // This should run on mount
    // Also when a below dep changes.
    // `value` is in `handleOnInput`
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isRequired, name, onFormElementChange])

  const handleOnInput = React.useCallback(
    (e: FormEvent<HTMLInputElement>) => {
      if (onInput) {
        onInput(e)
      }

      if (onFormElementChange) {
        onFormElementChange(
          name,
          `${e.currentTarget.value ?? ''}`,
          isRequired ?? false,
          validations,
        )
      }
    },
    [isRequired, name, onInput, onFormElementChange, validations],
  )

  return (
    <Wrapper className={clientErrorText ? 'error' : undefined}>
      {label && (
        <LabelContainer>
          <Label htmlFor={name}>{label}</Label>
        </LabelContainer>
      )}
      <Input
        onInput={handleOnInput}
        max={MAX_DATE}
        min={MIN_DATE}
        name={name}
        {...props}
        ref={inputRef}
        type="date"
      />
      {clientErrorText && <p>{clientErrorText}</p>}
    </Wrapper>
  )
}

const Wrapper = styled.div(({ theme }) => ({
  '&.error': {
    color: theme.error,
    '& input': {
      borderColor: theme.error,
    },
    '& label': {
      color: 'inherit',
    },
    '& p': {
      color: 'inherit',
      fontSize: 12,
      margin: '3px 0 0 2px',
    },
  },
}))

const Input = styled.input(
  {
    '&:disabled': {
      cursor: 'not-allowed',
    },
    ...elementProps,
    boxShadow: 'unset', // kills red around 'required' inputs in FF
    lineHeight: 1.3,
    overflow: 'hidden',
    padding: '14px 8px',
    resize: 'none',
  },
  ({ theme }) => ({
    backgroundColor: theme.background,
    border: `1px solid ${theme.hover}`,
    borderRadius: 4,
    color: theme.text,
    fontFamily: theme.fontFamily,
  }),
)

const LabelContainer = styled.div({
  marginBottom: 4,
  display: 'flex',
})

const Label = styled.label({
  fontSize: 12,
  fontWeight: 'bold',
  flex: '1 0 auto',
})

export default DateField
